import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  fetch_instance,
  getAccessToken,
  ServerURL,
} from '../__fakeApi__/ServerConfig';
import useAuth from '../hooks/useAuth';

const SpecialPermissionContext = createContext({
  can: (permissions) => false,
  specialPermissions: [],
});

SpecialPermissionContext.displayName = 'Special Permissions Context';

function SpecialPermissionProvider(props) {
  const { children } = props;

  const { isAuthenticated } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [specialPermissions, setSpecialPermissions] = useState([]);

  const fetchSpecialPermissions = async () => {
    setIsLoading(true);

    try {
      const response = await fetch_instance(
        `${ServerURL}special-permissions/current-user-permissions`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        }
      );

      const responseJson = await response.json();

      if (!response.ok) {
        throw responseJson;
      }

      setSpecialPermissions(responseJson);
    } catch (error) {
      toast.error(error.message || error.error || JSON.stringify(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSpecialPermissions();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const can = (permissions) => {
    if(Array.isArray(permissions) === false) {
      return false
    }

    return permissions.every((permission) =>
      specialPermissions.some((item) => item.code === permission)
    );
  };

  const contextValue = useMemo(
    () => ({
      can,
      specialPermissions,
    }),
    [specialPermissions]
  );

  return (
    <SpecialPermissionContext.Provider value={contextValue}>
      {children}
    </SpecialPermissionContext.Provider>
  );
}

SpecialPermissionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useSpecialPermissionsContext() {
  return useContext(SpecialPermissionContext);
}

export default SpecialPermissionProvider;

export { useSpecialPermissionsContext };
